import React, { useMemo } from "react";
import { Box, Button, Card, CardHeader } from "@mui/material";
import { muiTheme } from "@/styles/theme";
import { Plus } from "lucide-react";
import { getTargetMultipliers } from "@/modules/targets/api/index.v2";
import { useQuery } from "@tanstack/react-query";
import { getCurrentQuarter } from "@/modules/targets/utils";
import useCommonTable from "@/modules/targets/hooks/common-table-hook";
import CommonTable from "@/modules/common/components/tables/common-table/body/common-table";
import WithErrorHandling from "@/modules/common/components/wrappers/with-error-handling";
import { CommonTableColumns } from "@/modules/common/types";

interface TargetsMultiplierProps {
  onCreateTargetMultiplier: () => void;
  onError?: () => void;
  organizationUId: string | undefined;
}

interface Row {
  Rank: {
    title: string;
  };
  SubDepartment: {
    name: string;
  };
  count: number;
  percentage: number;
}

const TargetsMultiplier: React.FC<TargetsMultiplierProps> = ({
  onCreateTargetMultiplier,
  onError,
  organizationUId,
}) => {
  const currentQuarter = getCurrentQuarter();

  const {
    data: multipliers,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["target-multipliers", organizationUId],
    queryFn: () =>
      getTargetMultipliers({
        from: currentQuarter.startDate,
        to: currentQuarter.endDate,
        organizationUIds: [organizationUId],
      }),
    enabled: !!organizationUId,
  });

  const tableData = useMemo(() => {
    return Array.isArray(multipliers?.data) ? multipliers.data : [];
  }, [multipliers]);

  const targetMultiplierColumns: CommonTableColumns[] = [
    {
      id: "rank",
      Header: "Rank",
      accessor: (row: Row) => row?.Rank?.title,
      Cell: ({
        row,
      }: {
        row: {
          original: Row;
        };
      }) => {
        return <Box sx={{ minHeight: 40 }}>{row.original?.Rank?.title}</Box>;
      },
      minWidth: 150,
    },
    {
      id: "subdepartment",
      Header: "Subdepartment",
      accessor: (row: Row) => row?.SubDepartment?.name,
      minWidth: 150,
    },
    {
      id: "reports",
      Header: "Reports",
      accessor: (row: Row) => row?.count,
      minWidth: 150,
    },
    {
      id: "amount",
      Header: "Multiplier",
      accessor: (row: Row) => row?.percentage,
      minWidth: 150,
    },
  ];

  const {
    columns,
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
  } = useCommonTable(tableData || [], targetMultiplierColumns);

  if (isError && onError) {
    onError();
  }

  return (
    <Card
      sx={{
        bgcolor: muiTheme.colorSchemes.light.palette.main.white,
        boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
        borderRadius: 2,
      }}
    >
      <CardHeader
        title={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                color: muiTheme.colorSchemes.light.palette.neutral[900],
                ...muiTheme.typography.h3_med,
              }}
            >
              Targets Multiplier
            </Box>
            <Button
              variant="outlined"
              fullWidth
              startIcon={
                <Plus
                  style={{
                    color: muiTheme.colorSchemes.light.palette.primary[500],
                  }}
                />
              }
              sx={{
                color: muiTheme.colorSchemes.light.palette.primary[500],
                borderColor: muiTheme.colorSchemes.light.palette.neutral[100],
                bgcolor: "#ffffff",
                textTransform: "none",
                height: 30,
                width: "fit-content",
                "&:hover": {
                  borderColor: muiTheme.colorSchemes.light.palette.primary[500],
                  bgcolor: muiTheme.colorSchemes.light.palette.neutral[100],
                },
              }}
              onClick={onCreateTargetMultiplier}
            >
              Add
            </Button>
          </Box>
        }
      />
      <CommonTable
        headerGroups={headerGroups}
        prepareRow={prepareRow}
        rows={rows}
        getTableProps={getTableProps}
        getTableBodyProps={getTableBodyProps}
        columns={columns}
        isLoading={isLoading}
        tableName="Target Multipliers"
      />
    </Card>
  );
};

export default WithErrorHandling(TargetsMultiplier);
